import { ChangeDetectorRef, inject, Injectable, OnDestroy, PendingTasks } from '@angular/core';
import { TypedSubject } from '@bcf-vanilla-ts-v1-platforms/platform-worker/browser/types';
import { asapScheduler, MonoTypeOperatorFunction, Observable, Subscription, tap } from 'rxjs';
import { ExtractedObservable, WorkerBlocChannels } from '../common/types';
import { WorkerBlocId } from '../worker-blocs.type';
import { BrowserSideBlocController } from './browser-side-bloc-controller';
import { Unsubscribable } from './unsubscribable';

type PendingTaskDoneFn = () => void;

type SecondRxKeys<R2 extends WorkerBlocChannels> = keyof R2['rx'];
type SecondSubjectKeys<R2 extends WorkerBlocChannels> = keyof R2['subjects'];

@Injectable()
export class UnsubscribableWithWorkerBloc2<T, R1 extends WorkerBlocChannels, R2 extends WorkerBlocChannels>
  extends Unsubscribable
  implements OnDestroy
{
  private _uniqueId!: string;
  private _workerBlocId!: WorkerBlocId;

  private _secondUniqueId: string | undefined;
  private _secondWorkerBlocId: WorkerBlocId | undefined;

  private _browserSideBlocController: BrowserSideBlocController<R1 | R2> = inject(BrowserSideBlocController);
  protected _cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private _pendingTasks: PendingTasks = inject(PendingTasks);
  private _readyWhenPropsSet: Array<keyof R1['rx']> = [];
  private _readyWhenPropsReceived: Record<string, boolean | undefined> = {};
  private _isReady: boolean = false;
  private _pendingTasksMemory: Record<string, PendingTaskDoneFn | undefined> = {};

  private static readonly _secondReadyKeyPrefix: string = '__w2__:';

  private _secondReadyWhenPropsSet: Array<SecondRxKeys<R2>> = [];
  private _secondReadyWhenPropsReceived: Record<string, boolean | undefined> = {};
  private _secondIsReady: boolean = false;

  protected _initWorkerBloc(workerBlocId: WorkerBlocId): void {
    this._workerBlocId = workerBlocId;
    this._uniqueId = BrowserSideBlocController.getId(workerBlocId);
    this._browserSideBlocController.sendCreateInstance(workerBlocId, this._uniqueId);
    asapScheduler.schedule(() => this._cdRef.detectChanges(), 1);
    this._sub = this._browserSideBlocController
      .workerBlocInitialized(this._workerBlocId, this._uniqueId)
      .subscribe(() => this._initializedBloc());
  }

  protected _initSecondWorkerBloc(workerBlocId: WorkerBlocId): void {
    this._secondWorkerBlocId = workerBlocId;
    this._secondUniqueId = BrowserSideBlocController.getId(workerBlocId);
    this._browserSideBlocController.sendCreateInstance(workerBlocId, this._secondUniqueId);
    asapScheduler.schedule(() => this._cdRef.detectChanges(), 1);
    this._sub = this._browserSideBlocController
      .workerBlocInitialized(this._secondWorkerBlocId, this._secondUniqueId)
      .subscribe(() => this._initializedSecondBloc());
  }

  public init(_arg1?: unknown, _arg2?: unknown, _arg4?: unknown): void {}

  protected _initializedBloc(): void {}

  protected _initializedSecondBloc(): void {}

  private _secondReadyPropKey(key: string): string {
    return UnsubscribableWithWorkerBloc2._secondReadyKeyPrefix + key;
  }

  protected _setupReadyWhenPropsReceived(props: Array<keyof R1['rx']>): void {
    this._completePendingReadyTask(this._uniqueId);
    this._readyWhenPropsSet = props;
    this._readyWhenPropsReceived = {};
    this._isReady = false;
    this._pendingTasksMemory[this._uniqueId] = this._pendingTasks.add();
  }

  protected _setupReadyWhenPropsReceivedSecond(props: Array<SecondRxKeys<R2>>): void {
    if (!this._secondUniqueId) {
      throw new Error(
        'Second worker bloc not initialized; call _initSecondWorkerBloc before _setupReadyWhenPropsReceivedSecond.'
      );
    }
    this._completePendingReadyTask(this._secondUniqueId);
    this._secondReadyWhenPropsSet = props;
    this._secondReadyWhenPropsReceived = {};
    this._secondIsReady = false;
    this._pendingTasksMemory[this._secondUniqueId] = this._pendingTasks.add();
  }

  private _markReadyWhenTracked(key: string): void {
    if (this._readyWhenPropsSet.length === 0) {
      return;
    }
    if (!this._readyWhenPropsSet.some((prop: keyof R1['rx']) => (prop as string) === key)) {
      return;
    }
    this._readyWhenPropsReceived[key] = true;
    this._checkUiReady();
  }

  private _markSecondReadyWhenTracked(key: string): void {
    if (this._secondReadyWhenPropsSet.length === 0) {
      return;
    }
    if (!this._secondReadyWhenPropsSet.some((prop: SecondRxKeys<R2>) => (prop as string) === key)) {
      return;
    }
    this._secondReadyWhenPropsReceived[this._secondReadyPropKey(key)] = true;
    this._checkSecondUiReady();
  }

  private _checkUiReady(): void {
    if (this._isReady) {
      return;
    }
    if (this._readyWhenPropsSet.length === 0) {
      return;
    }
    const allIsReady: boolean = this._readyWhenPropsSet.every(
      (key: keyof R1['rx']) => this._readyWhenPropsReceived[key as string]
    );
    if (allIsReady) {
      this._isReady = true;
      this._completePendingReadyTask(this._uniqueId);
    }
  }

  private _checkSecondUiReady(): void {
    if (this._secondIsReady) {
      return;
    }
    if (this._secondReadyWhenPropsSet.length === 0) {
      return;
    }
    const allIsReady: boolean = this._secondReadyWhenPropsSet.every(
      (key: SecondRxKeys<R2>) => this._secondReadyWhenPropsReceived[this._secondReadyPropKey(key as string)]
    );
    if (allIsReady) {
      this._secondIsReady = true;
      this._completePendingReadyTask(this._secondUniqueId);
    }
  }

  private _completePendingReadyTask(uniqueId: string | undefined): void {
    if (!uniqueId) {
      return;
    }
    const completePendingTask: PendingTaskDoneFn | undefined = this._pendingTasksMemory[uniqueId];
    if (!completePendingTask) {
      return;
    }

    this._pendingTasksMemory[uniqueId] = undefined;
    completePendingTask();
  }

  protected _setReadyProp(key: keyof R1['rx']): void {
    this._markReadyWhenTracked(key as string);
  }

  protected _tapAndSetReadyProp<X>(key: keyof R1['rx']): MonoTypeOperatorFunction<X> {
    return tap(() => {
      this._markReadyWhenTracked(key as string);
    });
  }

  protected _setReadyPropSecond(key: SecondRxKeys<R2>): void {
    this._markSecondReadyWhenTracked(key as string);
  }

  protected _tapAndSetReadyPropSecond<X>(key: SecondRxKeys<R2>): MonoTypeOperatorFunction<X> {
    return tap(() => {
      this._markSecondReadyWhenTracked(key as string);
    });
  }

  protected _set<K extends keyof T>(key: K) {
    return <V extends T[K]>(value: V): void => {
      const that: T = this as any as T;
      that[key] = value;
      this._cdRef.detectChanges();
      this._markReadyWhenTracked(key as string);
    };
  }

  protected _setResolvedPromise<K extends keyof T, V extends T[K]>(
    key: K,
    promise: Promise<V> | (() => Promise<V>)
  ): void {
    const resolvedPromise: Promise<V> = typeof promise === 'function' ? promise() : promise;
    resolvedPromise.then((value: V) => {
      const that: T = this as any as T;
      that[key] = value;
      this._cdRef.detectChanges();
      this._markReadyWhenTracked(key as string);
    });
  }

  protected _setFromSecondBloc<K extends keyof T>(key: K) {
    return <V extends T[K]>(value: V): void => {
      const that: T = this as any as T;
      that[key] = value;
      this._cdRef.detectChanges();
      this._markSecondReadyWhenTracked(key as string);
    };
  }

  protected _subTo<K extends keyof R1['rx'], V extends ExtractedObservable<R1['rx'][K]>>(channelRx: K): Observable<V> {
    return this._browserSideBlocController.subTo(this._workerBlocId, this._uniqueId, channelRx);
  }

  protected _subToAndSubscribe<K extends keyof R1['rx'], Z extends keyof T, V extends ExtractedObservable<R1['rx'][K]>>(
    channelRx: K & Z
  ): Subscription {
    // TODO: fix any
    return this._subTo<K, V>(channelRx).subscribe(this._set(channelRx as any));
  }

  protected _subToSecond<K extends SecondRxKeys<R2>, V extends ExtractedObservable<R2['rx'][K]>>(
    channelRx: K
  ): Observable<V> {
    if (!this._secondWorkerBlocId || !this._secondUniqueId) {
      throw new Error('Second worker bloc not initialized; call _initSecondWorkerBloc first.');
    }
    return this._browserSideBlocController.subTo(this._secondWorkerBlocId, this._secondUniqueId, channelRx);
  }

  protected _subToAndSubscribeSecond<K extends SecondRxKeys<R2> & keyof T, V extends ExtractedObservable<R2['rx'][K]>>(
    channelRx: K
  ): Subscription {
    // TODO: fix any
    return this._subToSecond<K, V>(channelRx).subscribe(this._setFromSecondBloc(channelRx as keyof T));
  }

  protected _workerSubject<V extends TypedSubject<R1['subjects'][K]>, K extends keyof R1['subjects']>(
    subjectKey: K
  ): V {
    return this._browserSideBlocController.workerSubject(this._workerBlocId, () => this._uniqueId, subjectKey);
  }

  protected _workerObservable<K extends keyof R1['rx'], V extends ExtractedObservable<R1['rx'][K]>>(
    channelRx: K
  ): Observable<V> {
    return this._browserSideBlocController.subTo(this._workerBlocId, this._uniqueId, channelRx);
  }

  protected _workerSubjectSecond<V extends TypedSubject<R2['subjects'][K]>, K extends SecondSubjectKeys<R2>>(
    subjectKey: K
  ): V {
    if (!this._secondWorkerBlocId) {
      throw new Error('Second worker bloc not initialized; call _initSecondWorkerBloc first.');
    }
    return this._browserSideBlocController.workerSubject(
      this._secondWorkerBlocId,
      () => this._secondUniqueId as string,
      subjectKey
    );
  }

  protected _workerObservableSecond<K extends SecondRxKeys<R2>, V extends ExtractedObservable<R2['rx'][K]>>(
    channelRx: K
  ): Observable<V> {
    if (!this._secondWorkerBlocId || !this._secondUniqueId) {
      throw new Error('Second worker bloc not initialized; call _initSecondWorkerBloc first.');
    }
    return this._browserSideBlocController.subTo(this._secondWorkerBlocId, this._secondUniqueId, channelRx);
  }

  public override ngOnDestroy(): void {
    this._completePendingReadyTask(this._uniqueId);
    this._completePendingReadyTask(this._secondUniqueId);
    super.ngOnDestroy();
    this._browserSideBlocController.sendDestroyInstance(this._workerBlocId, this._uniqueId);
    if (this._secondWorkerBlocId && this._secondUniqueId) {
      this._browserSideBlocController.sendDestroyInstance(this._secondWorkerBlocId, this._secondUniqueId);
    }
  }
}
